import { array, monoid, nonEmptyArray, option, record, string } from 'fp-ts';
import { flow, pipe } from 'fp-ts/function';
import { constant, flatten, get, map, mapValues, times } from 'lodash/fp';
import { createSelector } from 'reselect';
import { getStringProp, values } from '@mews/fp';
import { createPropsExtender, DateTimeUtils } from '@mews/framework';
import { ApplicationState } from '../../../../types';
import { WithReservationId } from '../../../../types/selectors/selectorPropTypes';
import { toUtcWithoutMiliseconds } from '../../../../utils/dates';
import { getAvailabilityHash } from '../../../../utils/getAvailabilityHash';
import {
    getAvailableCategoryCount,
    getHourlyAvailableCategoryCount,
    getHourlyEndDate,
    toEndDateUtc,
} from '../../../serviceAvailability/createCategoriesAvailability/createCategoriesAvailability';
import {
    checkIsOvernightBookingEnabled,
    getServiceAvailabilityGroupedByEnterpriseId,
} from '../../../serviceAvailability/selectors';
import { getUniqueAgeCategoriesPerClassification } from '../../ageCategories/selectors';
import { getEnterprises } from '../../enterprises/selectors';
import { getEntities } from '../../getEntities';
import { getReservationPriceMeta } from '../../reservationPrices/selectors';
import { getDayUseServiceEnterpriseIds } from '../../services/selectors';
import { Entities } from '../../types';
import { getIdsHash } from '../getIdsHash';
import { Reservation, Reservations } from '../types';

export const getReservations = createSelector<ApplicationState, Entities, Reservations>(
    getEntities,
    get('reservations')
);

interface ReservationRoom {
    roomId: string;
    rateId: string;
    count: number;
}

const reservationRoomMonoid: monoid.Monoid<ReservationRoom> = {
    concat: (x, y) => ({
        roomId: y.roomId,
        rateId: y.rateId,
        count: x.count + y.count,
    }),
    empty: { roomId: '', rateId: '', count: 0 },
};
/**
 * Groups reservations by roomId and rateId and returns unique total count for each group.
 */
export const getUniqueRoomRateReservationsCount = createSelector(
    getReservations,
    flow(
        values,
        nonEmptyArray.groupBy((reservation) => `${reservation.roomId}-${reservation.rateId}`),
        record.map(nonEmptyArray.concatAll(reservationRoomMonoid)),
        values
    )
);

export const getReservationIds = createSelector(
    getReservations,
    flow(
        values,
        array.map(({ orderId }) => orderId)
    )
);

export const getReservation = createSelector<
    ApplicationState,
    WithReservationId,
    string,
    Reservations,
    Reservation | undefined
>(getStringProp('reservationId'), getReservations, get);

export const getReservationsList = flow<
    [ApplicationState],
    Reservations,
    Record<string, Array<Reservation>>,
    Array<Array<Reservation>>,
    Array<Reservation>
>(
    getReservations,
    mapValues<Reservation, Array<Reservation>>((reservation) => times(constant(reservation), reservation.count)),
    values,
    flatten
);

export const getCurrentReservationGroupId = flow(getReservationsList, map('orderId'), getIdsHash);

export const currentReservationGroupIdPropsExtender = createPropsExtender(
    'reservationGroupId',
    getCurrentReservationGroupId
);

export const hasCurrentReservationGroupPricesFailed = createSelector(
    getCurrentReservationGroupId,
    getReservationPriceMeta,
    (reservationGroupId, meta) => Boolean(meta?.[reservationGroupId]?.hasFailed)
);

export const getReservationTotalPersonCount = createSelector(getReservation, (reservation) => {
    if (reservation) {
        const { occupancyData, adultCount, childCount, count } = reservation;
        // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
        if (occupancyData) {
            return pipe(
                occupancyData,
                array.reduce(0, (occupancyCount, { personCount }) => (occupancyCount + personCount) * count)
            );
        }
        return ((adultCount ?? 0) + (childCount ?? 0)) * count;
    }
    return 0;
});

export const getTotalReservationOccupancyData = createSelector(
    getReservation,
    getUniqueAgeCategoriesPerClassification,
    (reservation, { adultAgeCategoryId, childAgeCategoryId }) => {
        if (reservation) {
            const { occupancyData, adultCount, childCount, count } = reservation;

            return (
                occupancyData ?? [
                    { ageCategoryId: adultAgeCategoryId ?? '', personCount: (adultCount ?? 0) * count },
                    { ageCategoryId: childAgeCategoryId ?? '', personCount: (childCount ?? 0) * count },
                ]
            );
        }
        return [];
    }
);

export const getReservationProductIds = createSelector(getReservation, (reservation) => {
    if (reservation) {
        const { productIds } = reservation;
        return pipe(productIds, array.uniq(string.Eq));
    }
    return [];
});

type ResourceOrderAvailableCounts = Record<string, Record<string, number>>;

export const getResourceOrderAvailableCounts = createSelector(
    getReservations,
    getServiceAvailabilityGroupedByEnterpriseId,
    getEnterprises,
    getDayUseServiceEnterpriseIds,
    (reservations, serviceAvailability, enterprises, dayUseServiceEnterpriseIds) =>
        pipe(
            reservations,
            values,
            /**
             * Use availability hashes as keys in accumulator.
             */
            array.reduce<Reservation, ResourceOrderAvailableCounts>({}, (acc, reservation) => {
                const {
                    roomId: categoryId,
                    startDate,
                    endDate,
                    hotelId: enterpriseId,
                    voucherCode,
                    startTimeUtc,
                    endTimeUtc,
                } = reservation;
                const availabilityHash = getAvailabilityHash({
                    startDate: startTimeUtc ?? startDate,
                    endDate: endTimeUtc ?? endDate,
                    enterpriseId,
                    voucherCode,
                });
                const enterpriseTimezone = enterprises[enterpriseId]?.ianaTimeZoneIdentifier;
                if (enterpriseTimezone == null) {
                    return acc;
                }
                const isDayUse = dayUseServiceEnterpriseIds.includes(enterpriseId);
                const startTime =
                    startTimeUtc != null
                        ? DateTimeUtils.dateTimeUtcStringToTime(startTimeUtc, enterpriseTimezone)
                        : undefined;
                const endTime =
                    endTimeUtc != null
                        ? DateTimeUtils.dateTimeUtcStringToTime(endTimeUtc, enterpriseTimezone)
                        : undefined;
                const isHourlyService = startTime != null && endTime != null;
                const endDateUtc = toEndDateUtc(endDate, enterpriseTimezone, isDayUse || isHourlyService);
                const startDateUtc = toUtcWithoutMiliseconds(startDate, enterpriseTimezone);
                const availabilityCount = pipe(
                    serviceAvailability,
                    record.lookup(enterpriseId),
                    option.flatMap(
                        array.findFirst((categoryAvailability) => categoryAvailability.categoryId === categoryId)
                    ),
                    option.map((categoryAvailability) => {
                        const isOvernightBookingEnabled =
                            isHourlyService &&
                            checkIsOvernightBookingEnabled([categoryAvailability], enterpriseTimezone);
                        const endUtc =
                            endTimeUtc != null && isOvernightBookingEnabled ? getHourlyEndDate(endTimeUtc) : endDateUtc;

                        return isHourlyService &&
                            categoryAvailability.availableStartTimes != null &&
                            !isOvernightBookingEnabled
                            ? getHourlyAvailableCategoryCount({
                                  availableStartTimes: categoryAvailability.availableStartTimes[startDateUtc],
                                  startTime,
                                  endTime,
                              })
                            : getAvailableCategoryCount({
                                  startDateUtc: startTimeUtc ?? startDateUtc,
                                  endDateUtc: endUtc,
                                  timeUnitsAvailability: categoryAvailability.timeUnitsAvailability,
                              });
                    }),
                    option.getOrElse(() => 0)
                );

                acc[availabilityHash] = { ...acc[availabilityHash], [categoryId]: availabilityCount };
                return acc;
            })
        )
);

export const areReservationsWithPromotedServices = createSelector(
    getReservations,
    flow(
        values,
        array.some(({ promotedServices }) => array.isNonEmpty(promotedServices))
    )
);
